/** choice / reduce / complexity-quiz の共通部分（card-schema.md §3.4〜§3.6、judge.md §4.3） */
import {
  reduceOptionLabel,
  shuffledOrder,
  type ChoiceCard,
  type ComplexityQuizCard,
  type ReduceCard,
} from '../../lib/cards';
import { formatCount, predictFromConstraints, BAND_LABELS } from '../../lib/complexity';
import type { ChoiceResult } from '../../lib/judge';
import type { GradeResult } from '../types';

export type AnyChoiceCard = ChoiceCard | ReduceCard | ComplexityQuizCard;

export interface ChoiceItem {
  /** YAML の並び順の添字 */
  index: number;
  label: string;
  /** label をインライン markdown として描くか（reduce の手法名は描かない） */
  markdown: boolean;
  correct: boolean;
  accepted: boolean;
  explanation?: string;
}

/** YAML の並び順の選択肢 */
export function choiceItems(card: AnyChoiceCard): ChoiceItem[] {
  if (card.type === 'reduce') {
    return card.choices.map((c, index) => ({
      index,
      label: reduceOptionLabel(c),
      markdown: false,
      correct: c.correct,
      accepted: c.accepted === true,
      ...(c.explanation !== undefined ? { explanation: c.explanation } : {}),
    }));
  }
  return card.choices.map((c, index) => ({
    index,
    label: c.text,
    markdown: true,
    correct: c.correct,
    accepted: c.accepted === true,
    ...(c.explanation !== undefined ? { explanation: c.explanation } : {}),
  }));
}

/** 表示順（id を種にした決定的なシャッフル） */
export function displayItems(card: AnyChoiceCard): ChoiceItem[] {
  const items = choiceItems(card);
  return shuffledOrder(card.id, items.length).flatMap((i) => {
    const item = items[i];
    return item ? [item] : [];
  });
}

export function isMultiple(card: AnyChoiceCard): boolean {
  return card.type !== 'reduce' && card.multiple === true;
}

/** 回答後の 1 文目（judge.md §4.3: 許容なら第一候補の名前を出す） */
export function choiceMessage(
  items: readonly ChoiceItem[],
  result: ChoiceResult,
  multiple: boolean,
): GradeResult {
  const firsts = items.filter((i) => i.correct).map((i) => `「${i.label}」`);
  const names = firsts.join('');
  if (result === 'correct') {
    return { verdict: 'AC', message: multiple ? '選んだ組み合わせが一致しました。' : '正解です。' };
  }
  if (result === 'accepted') {
    return { verdict: 'AC', message: `正解（許容）です。第一候補は${names}です。` };
  }
  return {
    verdict: 'WA',
    message: `正解は${names}です。このカードはあとでもう一度出ます。`,
  };
}

/** 制約の表示（例「1 ≤ N ≤ 200,000」） */
export function constraintText(c: { name: string; min?: number; max: number }): string {
  const max = c.max.toLocaleString('ja-JP');
  return c.min !== undefined
    ? `${c.min.toLocaleString('ja-JP')} ≤ ${c.name} ≤ ${max}`
    : `${c.name} ≤ ${max}`;
}

/** 回答後に出す「制約からの見積もり」（card-schema.md §3.5, §3.6）。ops が無い・評価できないときは null */
export function predictionText(card: ReduceCard | ComplexityQuizCard): string | null {
  if (!card.ops || !card.constraints?.length) return null;
  try {
    const p = predictFromConstraints(card.ops, card.constraints);
    const at = card.constraints.map((c) => `${c.name} = ${formatCount(c.max)}`).join('、');
    return `${at} のとき約 ${p.countText} 回、${p.secondsText}（${BAND_LABELS[p.band]}）。`;
  } catch {
    return null;
  }
}
